import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import "./App.css";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { WhatsAppFloat } from "./components/WhatsAppFloat";
import { CookieConsent } from "./components/CookieConsent";
import { AnalyticsPageViews } from "./components/AnalyticsPageViews";
import { LangContext } from "./i18n/lang";
import Home from "./pages/Home";
import ServicesHub from "./pages/ServicesHub";
import ServicePage from "./pages/ServicePage";
import ProblemsHub from "./pages/ProblemsHub";
import ProblemPage from "./pages/ProblemPage";
import GuidesHub from "./pages/GuidesHub";
import GuidePage from "./pages/GuidePage";
import Prices from "./pages/Prices";
import Professionals from "./pages/Professionals";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Thanks from "./pages/Thanks";
import Panel from "./pages/Panel";
import Legal from "./pages/Legal";
import NotFound from "./pages/NotFound";

const ScrollManager = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
};

// Deriva el idioma de la URL (/en/... = inglés) y ajusta <html lang>.
const LangBridge = ({ children }) => {
  const { pathname } = useLocation();
  const lang = pathname === "/en" || pathname.startsWith("/en/") ? "en" : "es";
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);
  return <LangContext.Provider value={lang}>{children}</LangContext.Provider>;
};

const useLenis = () => {
  useEffect(() => {
    let lenis;
    let raf;
    let cancelled = false;
    import("lenis").then(({ default: Lenis }) => {
      if (cancelled) return;
      lenis = new Lenis({ duration: 1.1, smoothWheel: true });
      const loop = (time) => {
        lenis.raf(time);
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
    });
    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
      if (lenis) lenis.destroy();
    };
  }, []);
};

// Rutas públicas (sin /panel, que es privada y solo en español).
// Las rutas legales antiguas redirigen a las canónicas.
const publicRoutes = [
  { path: "/", el: <Home /> },
  { path: "/servicios/", el: <ServicesHub /> },
  { path: "/servicios/:slug/", el: <ServicePage /> },
  { path: "/problemas/", el: <ProblemsHub /> },
  { path: "/problemas/:slug/", el: <ProblemPage /> },
  { path: "/guias/", el: <GuidesHub /> },
  { path: "/guias/:slug/", el: <GuidePage /> },
  { path: "/precios/", el: <Prices /> },
  { path: "/profesionales/", el: <Professionals /> },
  { path: "/sobre-midefinca/", el: <About /> },
  { path: "/contacto/", el: <Contact /> },
  { path: "/gracias/", el: <Thanks /> },
  { path: "/aviso-legal/", el: <Legal page="aviso-legal" /> },
  { path: "/politica-de-privacidad/", el: <Legal page="politica-de-privacidad" /> },
  { path: "/politica-de-cookies/", el: <Legal page="politica-de-cookies" /> },
];

export function AppShell() {
  useLenis();
  return (
    <LangBridge>
      <div className="min-h-screen flex flex-col">
        <ScrollManager />
        <AnalyticsPageViews />
        <Header />
        <main className="flex-1">
          <Routes>
            <Route path="/panel/" element={<Panel />} />
            {publicRoutes.map((r) => (
              <Route key={`es${r.path}`} path={r.path} element={r.el} />
            ))}
            <Route path="/politica-privacidad/" element={<Navigate to="/politica-de-privacidad/" replace />} />
            <Route path="/politica-cookies/" element={<Navigate to="/politica-de-cookies/" replace />} />
            <Route path="/en" element={<Navigate to="/en/" replace />} />
            {publicRoutes.map((r) =>
              r.path === "/" ? (
                <Route key="en-home" path="/en/" element={r.el} />
              ) : (
                <Route key={`en${r.path}`} path={`/en${r.path}`} element={r.el} />
              )
            )}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        <Footer />
        <WhatsAppFloat />
        <CookieConsent />
      </div>
    </LangBridge>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}

export default App;
